import { ACTIVITY_LABELS, CALL_OUTCOME_LABELS, formatDate, formatTime, formatWeekday, israelDay, type ActivityType } from '@takumi/shared';
import {
  ArrowLeftRight, Banknote, CheckSquare, FileText, Globe, Mail, MessageCircle, NotebookPen, Package, Phone, Receipt, Settings2, ShoppingBag, Sparkles, Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { EmptyState, Skeleton } from '@/components/ui/primitives';
import type { Activity } from '@/lib/types';
import { cn } from '@/lib/utils';

const ICONS: Record<ActivityType, { icon: ReactNode; tone: string }> = {
  lead_created: { icon: <Sparkles />, tone: 'bg-gold-100 text-gold-700' },
  website_enquiry: { icon: <Globe />, tone: 'bg-tomorrow-bg text-tomorrow' },
  meta_lead: { icon: <Globe />, tone: 'bg-tomorrow-bg text-tomorrow' },
  tiktok_lead: { icon: <Globe />, tone: 'bg-tomorrow-bg text-tomorrow' },
  call: { icon: <Phone />, tone: 'bg-matcha-100 text-matcha-800' },
  whatsapp: { icon: <MessageCircle />, tone: 'bg-[#25d366]/15 text-[#128c4a]' },
  email: { icon: <Mail />, tone: 'bg-tomorrow-bg text-tomorrow' },
  note: { icon: <NotebookPen />, tone: 'bg-gold-50 text-gold-700' },
  task: { icon: <CheckSquare />, tone: 'bg-matcha-50 text-matcha-700' },
  meeting: { icon: <Users />, tone: 'bg-matcha-100 text-matcha-800' },
  sample: { icon: <Package />, tone: 'bg-matcha-100 text-matcha-800' },
  quote: { icon: <FileText />, tone: 'bg-gold-100 text-gold-700' },
  order: { icon: <ShoppingBag />, tone: 'bg-gold-100 text-gold-700' },
  invoice: { icon: <Receipt />, tone: 'bg-sunken text-ink-soft' },
  payment: { icon: <Banknote />, tone: 'bg-matcha-100 text-success' },
  stage_change: { icon: <ArrowLeftRight />, tone: 'bg-sunken text-ink-soft' },
  system: { icon: <Settings2 />, tone: 'bg-sunken text-muted' },
};

function dayLabel(day: string) {
  const today = israelDay();
  if (day === today) return 'היום';
  const d = new Date(day + 'T12:00:00Z');
  return `${formatWeekday(d)}, ${formatDate(day)}`;
}

export function Timeline({ items, loading }: { items: Activity[] | undefined; loading?: boolean }) {
  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!items?.length) return <EmptyState title="אין פעילות עדיין" description="שיחות, הודעות, הערות ושינויים יופיעו כאן" />;

  const groups: { day: string; items: Activity[] }[] = [];
  for (const a of items) {
    const day = israelDay(a.occurredAt);
    const last = groups.at(-1);
    if (last?.day === day) last.items.push(a);
    else groups.push({ day, items: [a] });
  }

  return (
    <ol className="flex flex-col gap-5 px-4 py-4" aria-label="ציר זמן">
      {groups.map((g) => (
        <li key={g.day}>
          <div className="mb-2 text-xs font-bold tracking-wide text-muted">{dayLabel(g.day)}</div>
          <ol className="relative flex flex-col gap-3 before:absolute before:inset-y-2 before:start-4 before:w-px before:bg-line">
            {g.items.map((a) => (
              <TimelineItem key={a.id} a={a} />
            ))}
          </ol>
        </li>
      ))}
    </ol>
  );
}

function TimelineItem({ a }: { a: Activity }) {
  const meta = ICONS[a.type];
  const outcome = a.metadata?.callOutcome;
  const headline = a.subject || ACTIVITY_LABELS[a.type];
  return (
    <li className="relative flex gap-3">
      <span className={cn('relative z-10 grid size-8 shrink-0 place-items-center rounded-full ring-4 ring-surface [&_svg]:size-4', meta.tone)}>{meta.icon}</span>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-semibold">{headline}</span>
          {a.subject && a.type !== 'stage_change' && a.type !== 'system' && <span className="text-xs text-muted">{ACTIVITY_LABELS[a.type]}</span>}
          {a.direction === 'inbound' && <span className="text-xs text-tomorrow">נכנס</span>}
          {outcome && <span className="rounded bg-sunken px-1.5 text-xs text-ink-soft">{CALL_OUTCOME_LABELS[outcome]}</span>}
          <span className="ms-auto text-xs text-faint tabular">{formatTime(a.occurredAt)}</span>
        </div>
        {a.body && <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap text-ink-soft">{a.body}</p>}
        <div className="mt-0.5 text-xs text-faint">
          {[a.userName ?? 'מערכת', a.contactName && `עם ${a.contactName}`].filter(Boolean).join(' · ')}
        </div>
      </div>
    </li>
  );
}
